import React from 'react'

import { Field } from 'redux-form/immutable'

import PrimaryButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/primaryButton/PrimaryButton'
import DropZone from '@lyrahealth-inc/ui-core/lib/atoms/form/dropZone/DropZone'
import MaskedInputField from '@lyrahealth-inc/ui-core/lib/atoms/form/maskedInputField/MaskedInputField'
import { CheckboxRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/Checkbox'
import { InputFieldRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { SelectFieldRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/SelectField'
import { IdCardIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/IdCardIcon'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './practiceInfoForm.module.scss'
import { states } from '../../../constants/appConstants'
import HiddenField from '../HiddenField'

const stateOptions = states.map((state) => ({ label: state.text, value: state.value }))
const paymentPreferenceOptions = [{ label: 'ACH', value: '2' }]

const dropZonePlaceholder = () => {
  return (
    <div className={styles['dropzone-placeholder']}>
      <IdCardIcon size={57} fillColor={styles.x_light_gray_100} />
      <span>Drag image here</span>
    </div>
  )
}

const dropZoneContext = (
  <div>Please upload a photo of your passport, driver’s license or ID card in JPEG or PNG formats</div>
)

const tineinTooltipContent = (
  <div>
    <strong>Numbers Only.</strong>
    <br />
    Omit Characters and Hyphens
  </div>
)

const nameOnAccountToolTipContent = 'Check Line 1 of your Form W-9 for the owner’s name or business’s name.'

type OwnProps = {
  handleSubmit?: (...args: any[]) => any
  submitting?: boolean
  submitFunction?: (...args: any[]) => any
  primaryButtonText?: string
  termsOfUseHandler?: (...args: any[]) => any
  showEmailField?: boolean
}

type Props = OwnProps & typeof PracticeInfoForm.defaultProps

class PracticeInfoForm extends React.Component<Props> {
  static defaultProps = {
    showEmailField: false,
  }

  render() {
    const { handleSubmit, submitting, submitFunction, primaryButtonText, termsOfUseHandler, showEmailField } =
      this.props

    return (
      // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      <form onSubmit={handleSubmit(submitFunction)}>
        <Field name='id' component={HiddenField} />
        {!showEmailField ? <Field name='email' component={HiddenField} /> : []}

        <div className='row'>
          {showEmailField ? (
            <div className='col-xs-12 col-sm-6'>
              <Field
                name='email'
                readOnly
                component={InputFieldRFF}
                label='Practice Email'
                placeholder='Email'
                testID={tID('PracticeInfoForm-email-field')}
              />
            </div>
          ) : (
            []
          )}
          <div className={`col-xs-12 ${showEmailField ? 'col-sm-6' : ''}`}>
            <Field
              name='name'
              readOnly
              component={InputFieldRFF}
              label='Practice Name'
              placeholder='Practice Name'
              testID={tID('PracticeInfoForm-name-field')}
            />
          </div>
        </div>
        <p className={styles.header}>Identity</p>
        <p>Enter the information of the practice’s authorized owner or operator for verification purposes.</p>
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='first_name'
              component={InputFieldRFF}
              label='First Name'
              placeholder='First Name'
              testID={tID('PracticeInfoForm-firstname-field')}
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='last_name'
              component={InputFieldRFF}
              label='Last Name'
              placeholder='Last Name'
              testID={tID('PracticeInfoForm-lastname-field')}
            />
          </div>
        </div>
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='phone'
              type='tel'
              component={MaskedInputField}
              mask='(111) 111-1111'
              placeholder='(XXX) XXX-XXXX'
              label='Practice Phone Number'
              data-test-id='PracticeInfoForm-phonenumber-field'
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='date_of_birth'
              type='tel'
              component={MaskedInputField}
              mask='11/11/1111'
              placeholder='MM/DD/YYYY'
              label='Date of Birth'
              data-test-id='PracticeInfoForm-dob-field'
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='ssn'
              type='tel'
              component={MaskedInputField}
              mask='111111111'
              label='Social Security Number'
              placeholder='XXXXXXXXX'
              data-test-id='PracticeInfoForm-ssn-field'
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-xs-12'>
            <Field
              name='identification_file_front'
              className={styles['id-dropzone']}
              component={DropZone}
              // @ts-expect-error TS(2769): No overload matches this call.
              placeholder={dropZonePlaceholder}
              context={dropZoneContext}
              label='Please Upload Front of Your Identification Document'
              dataTestId='PracticeInfoForm-idfront-field'
            />
          </div>
        </div>

        <div className='row'>
          <div className='col-xs-12'>
            <Field
              name='identification_file_back'
              className={styles['id-dropzone']}
              component={DropZone}
              // @ts-expect-error TS(2769): No overload matches this call.
              placeholder={dropZonePlaceholder}
              context={dropZoneContext}
              label='Please Upload Back of Your Identification Document'
              dataTestId='PracticeInfoForm-idback-field'
            />
          </div>
        </div>

        <h3>Tax Information</h3>
        <p>
          Enter the exact tax name (Line 1), taxpayer ID number, and address from your W-9. We’ll send important tax
          documents to the address you enter.
        </p>
        <p></p>
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='street'
              component={InputFieldRFF}
              label='Street Address'
              placeholder='Street'
              testID={tID('PracticeInfoForm-streetaddress-field')}
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='city'
              component={InputFieldRFF}
              label='City'
              placeholder='City'
              testID={tID('PracticeInfoForm-city-field')}
            />
          </div>
        </div>
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field name='state' component={SelectFieldRFF} label='State' options={stateOptions} placeholder='Select' />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='zip_code'
              type='tel'
              component={MaskedInputField}
              mask='11111'
              label='Zip Code'
              placeholder='Zip Code'
              data-test-id='PracticeInfoForm-zipcode-field'
            />
          </div>
        </div>
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='account_identifier'
              type='tel'
              tooltipContent={tineinTooltipContent}
              component={MaskedInputField}
              mask='111111111'
              label='TIN or EIN Number'
              placeholder='Digits only'
              data-test-id='PracticeInfoForm-tin-field'
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='account_name'
              component={InputFieldRFF}
              toolTipContent={nameOnAccountToolTipContent}
              label='Name on account (for tax purposes)'
              placeholder='Name on account (for tax purposes)'
              testID={tID('PracticeInfoForm-nameonaccount-field')}
            />
          </div>
        </div>
        <h3>Banking Information</h3>
        <p>Lyra will deposit your payments here.</p>
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='payment_preference'
              label='Payment Preference'
              component={SelectFieldRFF}
              options={paymentPreferenceOptions}
              placeholder='Select'
              useStringValue
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='bank'
              component={InputFieldRFF}
              label='Bank Name'
              placeholder='Bank Name'
              testID={tID('PracticeInfoForm-bankname-field')}
            />
          </div>
        </div>
        <div className='row'>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='routing_number'
              type='text'
              component={MaskedInputField}
              mask='111111111'
              label='Routing Number'
              placeholder='Routing Number'
              data-test-id='PracticeInfoForm-routingnumber-field'
            />
          </div>
          <div className='col-xs-12 col-sm-6'>
            <Field
              name='account_number'
              component={InputFieldRFF}
              label='Account Number'
              placeholder='Account Number'
              testID={tID('PracticeInfoForm-accountnumber-field')}
            />
          </div>
        </div>

        <div className={`row ${styles['consent-container']}`}>
          <Field
            name='tos'
            // @ts-expect-error TS(2769): RFF adapters are typed against react-final-form's FieldRenderProps, redux-form's Field expects WrappedFieldProps
            component={CheckboxRFF}
            testID={tID('PracticeInfoForm-tos-field')}
            title={
              <>
                I have read and understand Lyra Health's&nbsp;
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <span
                  id='termsOfServiceBtn'
                  onClick={(e) => {
                    // opening the terms must not also toggle the checkbox
                    if (termsOfUseHandler) {
                      e.stopPropagation()
                      termsOfUseHandler(e)
                    }
                  }}
                  className={styles['modal-link']}
                >
                  Terms of Use
                </span>
              </>
            }
          />
        </div>

        <div className='row'>
          <div className='col-xs-12'>
            <PrimaryButton
              id='test-practiceinfo-submit'
              isLoading={submitting}
              type='submit'
              // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
              onKeyDown={handleSubmit(submitFunction)}
              dataTestId='PracticeInfoForm-submit-button'
            >
              {primaryButtonText}
            </PrimaryButton>
          </div>
        </div>
      </form>
    )
  }
}

export default PracticeInfoForm
